import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Input, Modal, message, Button, Typography } from 'antd';
import AnimatedNumber from 'components/animated-number';
import { EyeOutlined, LockOutlined, DeleteOutlined } from '@ant-design/icons';
import { useI18n } from '../../../hooks/useI18n';
import {
	DASHBOARD_ACCESS_EVENT,
	clearDashboardAccess,
	grantDashboardAccess,
	hasDashboardProtection,
	isDashboardAccessGranted,
} from './dashboard-access';

const { Text } = Typography;

const DashboardCard = ({ span, icon, value, name, description, onClick, withPassword }) => {
	const { t } = useI18n();
	const [password, setPassword] = useState('');
	const [modalVisible, setModalVisible] = useState(false);
	const [clearPasswordModal, setClearPasswordModal] = useState(false);
	const [clearPassword, setClearPassword] = useState('');
	const correctPassword = localStorage.getItem('dashboardPassword');
	const [isPasswordChecked, setIsPasswordChecked] = useState(() => isDashboardAccessGranted());
	const hasPasswordProtection = hasDashboardProtection();
	const navigate = useNavigate();

	React.useEffect(() => {
		const syncDashboardAccess = () => {
			setIsPasswordChecked(isDashboardAccessGranted());
		};

		window.addEventListener(DASHBOARD_ACCESS_EVENT, syncDashboardAccess);
		window.addEventListener('storage', syncDashboardAccess);

		return () => {
			window.removeEventListener(DASHBOARD_ACCESS_EVENT, syncDashboardAccess);
			window.removeEventListener('storage', syncDashboardAccess);
		};
	}, []);

	const goToDashboardSettings = () => {
		setModalVisible(false);
		navigate('/settings#dashboard-password');
	};

	const handleClearPassword = () => {
		setClearPasswordModal(true);
	};

	const confirmClearPassword = () => {
		if (clearPassword !== correctPassword) {
			message.error(t('dashboard_card.incorrect_password'));
			return;
		}
		
		localStorage.removeItem('dashboardPassword');
		clearDashboardAccess();
		setClearPasswordModal(false);
		setModalVisible(false);
		setPassword('');
		setClearPassword('');
		setIsPasswordChecked(false);
		message.success(t('dashboard_card.clear_protection_success'));
		window.location.reload();
	};

	const handlePasswordSubmit = () => {
		if (!correctPassword) {
			grantDashboardAccess();
			setIsPasswordChecked(true);
			setModalVisible(false);
			setPassword('');
			return;
		}
		
		if (password === correctPassword) {
			grantDashboardAccess();
			setIsPasswordChecked(true);
			setModalVisible(false);
			setPassword('');
			message.success(t('dashboard_card.password_verified_success'));
		} else {
			message.error(t('dashboard_card.incorrect_password_try_again'));
		}
	};

	const handleModalCancel = () => {
		setModalVisible(false);
		setPassword('');
	};

	return (
		<Col xs={24} sm={12} lg={8} xl={span || 4} className="dashboard-card-wrapper">
			<div
				className="dashboard_card"
				onClick={onClick}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onClick?.();
					}
				}}
			>
				<div className="card-topline">
					<div className="metric-copy">
						<div className="metric-title">{name}</div>
						<div className="desc">{description}</div>
					</div>
					<div className="icon-wrapper">{icon}</div>
				</div>

				<div className="value-row">
					<div className="value">
					{withPassword && hasPasswordProtection ? (
						<>
							{isPasswordChecked ? (
								<span className="animated-value">
									<AnimatedNumber value={value} />
								</span>
							) : (
								<div
									className="password-locked"
									onClick={(e) => {
										e.stopPropagation();
										setModalVisible(true);
									}}
									title={t('dashboard.enter_password_to_view_data')}
								>
									<EyeOutlined />
								</div>
							)}
						</>
					) : (
						<span className="animated-value">
							<AnimatedNumber value={value} />
						</span>
					)}
					</div>
					{withPassword && hasPasswordProtection ? (
						<span className={`metric-state-chip ${isPasswordChecked ? 'metric-state-chip--unlocked' : 'metric-state-chip--locked'}`}>
							{isPasswordChecked ? 'Live' : 'Locked'}
						</span>
					) : null}
				</div>
			</div>

			<Modal
				title={
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<LockOutlined />
						{t('dashboard.enter_password_to_view_data')}
					</div>
				}
				open={modalVisible}
				onOk={handlePasswordSubmit}
				onCancel={handleModalCancel}
				width={550}
				centered
				footer={[
					<Button key="verify" type="primary" onClick={handlePasswordSubmit} style={{ marginRight: 8 }}>
						{t('dashboard_card.verify_password')}
					</Button>,
					<Button key="clear" danger onClick={handleClearPassword} style={{ marginRight: 8 }}>
						<DeleteOutlined /> {t('dashboard_card.clear_protection')}
					</Button>,
					<Button key="cancel" onClick={handleModalCancel}>
						{t('common.cancel')}
					</Button>,
				]}
			>
				<div style={{ padding: '16px 0' }}>
					<Input.Password
						prefix={<LockOutlined />}
						placeholder={t('dashboard_card.enter_current_password')}
						size="large"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						onPressEnter={handlePasswordSubmit}
						autoFocus
					/>
					<div style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
						{t('dashboard_card.password_instructions')}
					</div>
					<div style={{ marginTop: '8px' }}>
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								goToDashboardSettings();
							}}
							style={{ cursor: 'pointer', color: 'var(--primary-color, #4d7cfe)' }}
						>
							{t('dashboard_card.reset_manage_password')}
						</a>
					</div>
				</div>
			</Modal>

			<Modal
				title={t('dashboard_card.clear_protection_title')}
				open={clearPasswordModal}
				onOk={confirmClearPassword}
				onCancel={() => {
					setClearPasswordModal(false);
					setClearPassword('');
				}}
				okText={t('dashboard_card.clear_protection_confirm')}
				cancelText={t('common.cancel')}
				okButtonProps={{ danger: true }}
				centered
			>
				<div style={{ padding: '16px 0' }}>
					<p>{t('dashboard_card.clear_protection_question')}</p>
					<p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
						{t('dashboard_card.clear_protection_warning')}
					</p>
					<Input.Password
						prefix={<LockOutlined />}
						placeholder={t('dashboard_card.enter_current_password')}
						size="large"
						value={clearPassword}
						onChange={(e) => setClearPassword(e.target.value)}
						onPressEnter={confirmClearPassword}
						style={{ marginBottom: '8px' }}
					/>
					<Text type="secondary" style={{ fontSize: '12px' }}>
						{t('dashboard_card.enter_password_to_confirm')}
					</Text>
				</div>
			</Modal>
		</Col>
	);
};

export default DashboardCard;
